import React from 'react';
import styled, { css } from 'styled-components';
import Tippy from '@tippyjs/react';

import { formatNumber, isBrowser } from '@tager/web-core';

import InfoIcon from '@/assets/svg/white-info.svg';
import { colors } from '@/constants/theme';

interface Props {
  title: string;
  infoText?: string;
  value?: number;
  secondValue?: number;
  firstPayment?: number;
  lastPayment?: number;
  firstPrefix: string;
  secondPrefix?: string;
  active?: boolean;
}

function CreditInfo({
  title,
  infoText,
  firstPayment,
  lastPayment,
  value,
  secondValue,
  firstPrefix,
  secondPrefix,
  active,
}: Props) {
  return (
    <Component>
      <Header>
        <Title>{title}</Title>
        {infoText && (
          <Tippy
            content={
              <InfoBlock>
                <InfoText>{infoText}</InfoText>
              </InfoBlock>
            }
            placement="bottom"
            interactive
            zIndex={10001}
            appendTo={isBrowser() ? document.body : 'parent'}
          >
            <InfoIconWrapper>
              <InfoIcon />
            </InfoIconWrapper>
          </Tippy>
        )}
      </Header>

      <Body>
        {firstPayment ? (
          <Value active={active}>
            {firstPayment === lastPayment || !lastPayment
              ? formatNumber(Number(firstPayment))
              : 'от ' +
                formatNumber(Number(lastPayment)) +
                ' до ' +
                formatNumber(Number(firstPayment))}{' '}
            <Prefix active={active}>{firstPrefix}</Prefix>
          </Value>
        ) : (
          <Value active={active}>
            {formatNumber(value ?? 0)}{' '}
            <Prefix active={active}>{firstPrefix}</Prefix>
          </Value>
        )}

        {secondValue ? (
          <React.Fragment>
            <Slash>/</Slash>
            <Value secondValue={secondValue}>
              {formatNumber(Number(secondValue))}{' '}
              <Prefix secondValue={secondValue}>
                {secondPrefix ? secondPrefix : firstPrefix}
              </Prefix>
            </Value>
          </React.Fragment>
        ) : null}
      </Body>
    </Component>
  );
}

export default CreditInfo;

const Component = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
`;

const Title = styled.span`
  font-size: 12px;
  line-height: 14px;
  color: ${(props) => props.theme.black200_alpha40};
`;

const InfoBlock = styled.div`
  max-width: 316px;
  padding: 15px;
  background: ${colors.white};
  border: 0.5px solid ${colors.gray400};
  border-radius: 8px;
  box-shadow: ${(props) => props.theme.blackShadow100};
`;

const InfoText = styled.p`
  font-weight: 300;
  font-size: 12px;
  line-height: 16px;
  color: ${colors.black100};
`;

const InfoIconWrapper = styled.div`
  margin-left: 4px;
  width: 12px;
  height: 12px;

  svg {
    width: 100%;
    height: 100%;

    path {
      fill: ${(props) => props.theme.black100_alpha30};
    }
  }
`;

const Body = styled.div`
  display: flex;
  align-items: flex-end;
  margin-top: 4px;
`;

const Value = styled.p<{
  active?: Props['active'];
  secondValue?: Props['secondValue'];
}>`
  font-weight: 700;
  font-size: 17px;
  line-height: 23px;
  color: ${(props) => props.theme.black100};

  ${({ active }) =>
    active &&
    css`
      color: ${colors.main};
    `}

  ${({ secondValue }) =>
    secondValue &&
    css`
      color: ${(props) => props.theme.black200_alpha40};
    `}
`;

const Prefix = styled.span<{
  active?: Props['active'];
  secondValue?: Props['secondValue'];
}>`
  font-size: 13px;
  line-height: 20px;

  ${({ active }) =>
    active &&
    css`
      color: ${colors.main};
    `}

  ${({ secondValue }) =>
    secondValue &&
    css`
      color: ${(props) => props.theme.black200_alpha40};
    `}
`;

const Slash = styled.span`
  font-weight: 700;
  font-size: 14px;
  line-height: 23px;
  margin: 0 3px;
  color: ${(props) => props.theme.black200_alpha40};
`;
